import React, { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import {
  CustomField, CustomFieldType, CUSTOM_FIELD_TYPE_LABEL,
  listProjectCustomFields, createCustomField, deleteCustomField, fieldOptions,
} from '../../services/customFieldService';

// Custom field builder — Project Settings (PM-only, alongside the other
// project-level config sections). Defines fields that render as editable
// inputs in the task detail drawer's Custom Fields section.

export default function CustomFieldsSection({ pca, projectId }: {
  pca: IPublicClientApplication;
  projectId: string;
}) {
  const [fields, setFields] = useState<CustomField[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // builder form
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [fieldType, setFieldType] = useState<number>(CustomFieldType.Text);
  const [optionsDraft, setOptionsDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const reload = useCallback(async () => {
    try { setFields(await listProjectCustomFields(pca, projectId)); }
    catch (e) { setFields([]); setError(e instanceof Error ? e.message : 'Failed to load custom fields'); }
  }, [pca, projectId]);
  useEffect(() => { reload(); }, [reload]);

  const parsedOptions = optionsDraft.split(',').map(s => s.trim()).filter(Boolean);
  const isDropdown = fieldType === CustomFieldType.ChoiceSingle;
  const canSave = !!name.trim() && (!isDropdown || parsedOptions.length >= 2);

  const save = async () => {
    if (!canSave || saving || fields === null) return;
    if (fields.some(f => f.dagrun_pm_name.trim().toLowerCase() === name.trim().toLowerCase())) {
      setError(`A field named "${name.trim()}" already exists on this project.`);
      return;
    }
    setSaving(true); setError(null);
    try {
      const maxSort = fields.reduce((m, f) => Math.max(m, f.dagrun_pm_sortorder ?? 0), 0);
      await createCustomField(pca, {
        projectId,
        name: name.trim(),
        fieldType,
        options: isDropdown ? parsedOptions : undefined,
        sortOrder: maxSort + 1,
      });
      setName(''); setFieldType(CustomFieldType.Text); setOptionsDraft(''); setAdding(false);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create field');
    } finally { setSaving(false); }
  };

  const doDelete = async (f: CustomField) => {
    if (deleting) return;
    setDeleting(true); setError(null);
    try {
      await deleteCustomField(pca, f.dagrun_pm_customfieldid);
      setConfirmDeleteId(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to delete field');
    } finally { setDeleting(false); }
  };

  return (
    <div className="card" style={{ padding: 20, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div>
          <div className="eyebrow">CUSTOM FIELDS</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
            Project-specific fields that appear on every task's detail panel.
          </div>
        </div>
        {!adding && (
          <button className="btn-secondary" onClick={() => setAdding(true)}>+ Add Field</button>
        )}
      </div>

      {error && <div style={{ marginBottom: 10, fontSize: 12, color: '#DC2626' }}>{error}</div>}

      {adding && (
        <div style={{ padding: 12, background: 'var(--surface-2)', borderRadius: 6, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8, marginBottom: 8 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>Field Name *</label>
              <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Cost Code, Client Ref" />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>Type</label>
              <select value={fieldType} onChange={e => setFieldType(Number(e.target.value))}>
                <option value={CustomFieldType.Text}>Text</option>
                <option value={CustomFieldType.Decimal}>Number</option>
                <option value={CustomFieldType.Date}>Date</option>
                <option value={CustomFieldType.ChoiceSingle}>Dropdown</option>
              </select>
            </div>
          </div>
          {isDropdown && (
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>
                Options (comma-separated, at least 2)
              </label>
              <input value={optionsDraft} onChange={e => setOptionsDraft(e.target.value)} placeholder="e.g. Red, Amber, Green" />
              {parsedOptions.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {parsedOptions.map((o, i) => <span key={i} className="pill neutral" style={{ fontSize: 11 }}>{o}</span>)}
                </div>
              )}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <button type="button" onClick={() => { setAdding(false); setName(''); setOptionsDraft(''); setError(null); }} disabled={saving}
              style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--ink-3)', padding: '5px 12px', fontSize: 12, borderRadius: 4, cursor: 'pointer' }}
            >Cancel</button>
            <button type="button" onClick={save} disabled={saving || !canSave}
              style={{ background: '#0E7490', color: '#fff', border: 'none', padding: '5px 12px', fontSize: 12, fontWeight: 600, borderRadius: 4, cursor: canSave ? 'pointer' : 'not-allowed', opacity: saving || !canSave ? 0.6 : 1 }}
            >{saving ? 'Creating…' : 'Create Field'}</button>
          </div>
        </div>
      )}

      {fields === null ? (
        <div style={{ fontSize: 12, color: 'var(--ink-4)' }}>Loading…</div>
      ) : fields.length === 0 && !adding ? (
        <div style={{ padding: 16, textAlign: 'center', color: 'var(--ink-4)', fontSize: 13 }}>
          No custom fields yet.
        </div>
      ) : fields.length > 0 && (
        <div>
          {fields.map(f => {
            const opts = fieldOptions(f);
            return (
              <div key={f.dagrun_pm_customfieldid}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '0.5px solid var(--border-light)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>{f.dagrun_pm_name}</span>
                  {opts.length > 0 && (
                    <span style={{ fontSize: 11, color: 'var(--ink-4)', marginLeft: 8 }}>
                      {opts.join(' · ')}
                    </span>
                  )}
                </div>
                <span className="pill neutral" style={{ fontSize: 11 }}>
                  {CUSTOM_FIELD_TYPE_LABEL[f.dagrun_pm_fieldtype] ?? 'Field'}
                </span>
                {confirmDeleteId === f.dagrun_pm_customfieldid ? (
                  <span style={{ display: 'inline-flex', gap: 4 }}>
                    <button type="button" onClick={() => doDelete(f)} disabled={deleting}
                      style={{ background: '#DC2626', color: '#fff', border: 'none', padding: '3px 10px', fontSize: 11, fontWeight: 600, borderRadius: 4, cursor: 'pointer' }}
                    >{deleting ? 'Deleting…' : 'Delete field + values'}</button>
                    <button type="button" onClick={() => setConfirmDeleteId(null)} disabled={deleting}
                      style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--ink-3)', padding: '3px 10px', fontSize: 11, borderRadius: 4, cursor: 'pointer' }}
                    >Cancel</button>
                  </span>
                ) : (
                  <button type="button" onClick={() => setConfirmDeleteId(f.dagrun_pm_customfieldid)}
                    aria-label={`Delete ${f.dagrun_pm_name}`}
                    style={{ background: 'transparent', border: 'none', color: 'var(--ink-4)', cursor: 'pointer', fontSize: 15, lineHeight: 1 }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#DC2626')}
                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--ink-4)')}
                  >×</button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
